import axios, { AxiosInstance } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import type { TransferTokenResult } from './voiceSessionLifecycle';

// ── Personalization: Tokens ───────────────────────────────────────────

export type RecipientTokenRecord = {
  recipient_id: string;
  token: string;
  short_token: string;
  url: string;
  landing_url: string;
};

export type BatchTokenResult = {
  campaign_id: string;
  count: number;
  tokens: RecipientTokenRecord[];
};

export type BatchTokenRecordInput = {
  recipient_id: string;
  first_name: string;
  last_name?: string;
  phone?: string;
  email?: string;
};

export type BatchTokenInput = {
  campaign_id: string;
  expiry_days?: number;
  records: BatchTokenRecordInput[];
};

/** `POST /api/tokens/generate-batch` — generate personalized tokens for a list of contacts. */
export async function generateBatchTokens(
  input: BatchTokenInput,
  axiosInstance?: AxiosInstance
): Promise<BatchTokenResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<BatchTokenResult>(`${apiConfig.voiceEndpoints.tokens}/generate-batch`, input);
  return response.data;
}

/** Row from `GET /api/tokens?campaign_id=&limit=` (voice-agent-frontend `listTokens`). */
export type CampaignTokenListRow = {
  token?: string;
  short_token?: string;
  recipient_id?: string;
  scanned?: boolean;
  scan_count?: number;
  landing_url?: string;
  url?: string;
  profile?: Record<string, unknown>;
};

/** `GET /api/tokens` — list tokens / recipient links for a campaign. */
export async function fetchCampaignTokens(
  campaignId: string,
  opts: { limit?: number } = {},
  axiosInstance?: AxiosInstance,
): Promise<CampaignTokenListRow[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(apiConfig.voiceEndpoints.tokens, {
    params: { campaign_id: campaignId, limit: opts.limit ?? 500 },
  });
  const raw = response.data;
  const list = Array.isArray(raw) ? raw : raw?.items ?? raw?.tokens ?? [];
  return list as CampaignTokenListRow[];
}

/**
 * `POST /api/tokens/generate-batch` for a single contact.
 * Uses the `records` format (same as generateBatchTokens). Returns the personalized URL.
 */
export async function generateContactToken(
  campaignId: string,
  contact: { id: string; name: string; phone?: string; email?: string },
  axiosInstance?: AxiosInstance
): Promise<string | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const nameParts = (contact.name || '').trim().split(' ');
  const response = await client.post<BatchTokenResult>(
    `${apiConfig.voiceEndpoints.tokens}/generate-batch`,
    {
      campaign_id: campaignId,
      records: [{
        recipient_id: contact.id,
        first_name: nameParts[0] || contact.name,
        last_name: nameParts.slice(1).join(' ') || undefined,
        phone: contact.phone || undefined,
        email: contact.email || undefined,
      }],
    }
  );
  return response.data?.tokens?.[0]?.url ?? null;
}

/**
 * `GET /api/tokens/landing/{token}` — used by `/talk/t/{token}` before “Start Call”.
 * Customer-specific profile data is not in this payload; it is applied when starting the session.
 */
export type TokenLandingResult = {
  valid: boolean;
  campaign_id: string;
  campaign: {
    name: string;
    brand_name: string;
    brand_logo_url: string | null;
    /** Optional dial-in number; when present the landing page offers a "Call Now" (tel:) option. */
    phone_number?: string | null;
  };
};

/** Public endpoint — resolves a short token to campaign landing info. No auth required. */
export async function resolveToken(token: string): Promise<TokenLandingResult> {
  const client = getVoiceFallbackClient();
  const response = await client.get<TokenLandingResult>(`${apiConfig.voiceEndpoints.tokens}/landing/${token}`);
  return response.data;
}

/**
 * `POST /api/voice/session` with `channel: "webrtc"` and `recipient_token` (same string as in the URL).
 * ElevenLabs receives profile + Mem0 context server-side; use `signed_url` with `Conversation.startSession({ signedUrl })`.
 *
 * `dynamic_variables` mirror what the agent will resolve in greetings/prompt (`{{first_name}}`, `{{first_name_space}}`,
 * `{{name_prompt}}`, `{{caller_context}}`, `{{is_known_caller}}`, etc.). Prefer conditional copy via
 * `{{first_name_space}}` + `{{name_prompt}}` in `agent_greeting` — see personalization frontend guide.
 */
export type PersonalizedSessionRecord = {
  session_id: string;
  signed_url: string;
  dynamic_variables: Record<string, string>;
  /**
   * Per-call ElevenLabs voice A/B test variant (BOOSTT-3894 / P1-7), or null for the
   * agent's own default voice. Must be passed as a client SDK `startSession` override
   * (`overrides.tts.voiceId`) — the backend can no longer apply it itself, since it used
   * to do so by PATCHing the shared agent before minting the signed URL, which let two
   * concurrent calls with different variants clobber each other's voice setting.
   */
  voice_override_id: string | null;
};

/**
 * BOOSTT-3882 (P0-6) — capacity-overflow admission. `POST /api/voice/session` returns a plain
 * 200 `PersonalizedSessionRecord` on direct admission (normal/burst zone), or a 202 body shaped
 * like one of these three when the workspace is over_capacity. Axios doesn't throw on a 202, so
 * callers MUST check `'tier' in session` — none of these bodies has `signed_url`, so treating one
 * as a normal session would silently fail deeper in the ElevenLabs SDK call.
 *
 * None of these is an error outcome — high call volume is expected and each tier is a normal,
 * user-facing state:
 *  - `TRANSFER`: a human is available right now — bridge the caller straight to them.
 *  - `P2`: no human free, but the request already had a phone number — a callback was scheduled
 *    (and a confirmation text sent, unless the caller is opted out of SMS or it failed to send).
 *  - `PHONE_NEEDED`: a webrtc caller with no phone on file at all, and no human free — collect a
 *    phone number and resubmit `createPersonalizedSession` with `caller_phone` set, which lands
 *    on `TRANSFER` or `P2` depending on availability at that moment.
 *
 * `transfer`'s shape mirrors `TransferTokenResult` (the existing mid-call warm-transfer token) as
 * the only established pattern in this codebase for bridging a browser caller to a human via
 * Twilio — unconfirmed against the actual backend payload; the ticket explicitly invites adjusting
 * this shape with backend (routes/voice.py `_create_voice_session_impl`) if it doesn't match.
 */
export type CapacityDegradedSession =
  | { tier: 'TRANSFER'; transfer: TransferTokenResult }
  | { tier: 'P2'; callback_id: string; sms: 'sent' | 'skipped_opted_out' | 'failed' }
  | { tier: 'PHONE_NEEDED'; session_id: string; message: string };

export function isCapacityDegradedSession(
  session: PersonalizedSessionRecord | CapacityDegradedSession,
): session is CapacityDegradedSession {
  return 'tier' in session;
}

/**
 * Public endpoint — creates a personalized WebRTC session and returns the ElevenLabs signed URL,
 * or a capacity-overflow body (see CapacityDegradedSession) when the workspace is over capacity.
 * `caller_phone` is only needed to resubmit after a `PHONE_NEEDED` response.
 */
export async function createPersonalizedSession(input: {
  campaign_id: string;
  channel: 'webrtc';
  recipient_token: string;
  caller_phone?: string;
}): Promise<PersonalizedSessionRecord | CapacityDegradedSession> {
  const client = getVoiceFallbackClient();
  const response = await client.post<PersonalizedSessionRecord | CapacityDegradedSession>(`${apiConfig.voiceEndpoints.voice}/session`, input);
  return response.data;
}

/**
 * Fire-and-forget beacon reporting how a live/Preview call ended (the
 * @11labs/client SDK's 'disconnect'/'error' events). Feeds voice_ws_disconnect_total
 * so real call-end reasons are visible instead of invisible client-side state.
 * Never throws — a metrics beacon must not affect the caller's UI flow.
 */
export async function reportSessionClientMetric(
  sessionId: string,
  event: 'disconnect' | 'error',
  reason?: string
): Promise<void> {
  try {
    const client = getVoiceFallbackClient();
    await client.post(`${apiConfig.voiceEndpoints.voice}/session/${sessionId}/client-metrics`, { event, reason });
  } catch {
    // best-effort only
  }
}

export interface ResumeSessionResult {
  session_id: string;
  signed_url: string;
}

/**
 * Re-mint a signed URL for an existing session after a dropped WebSocket
 * connection (@11labs/client has no reliable auto-reconnect). Does NOT
 * re-run compliance/billing server-side — same billed session, not a new one.
 */
export async function resumeSession(sessionId: string): Promise<ResumeSessionResult> {
  const client = getVoiceFallbackClient();
  const response = await client.post<ResumeSessionResult>(
    `${apiConfig.voiceEndpoints.voice}/session/${sessionId}/resume`
  );
  return response.data;
}
